import React, { FC, useCallback, useContext, useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import Typography from '@mui/material/Typography'
import { Grid, Theme } from '@mui/material'
import pick from 'lodash/pick'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { IntroducerStatusEnum } from '@sparling/financial-common'
import { useDispatch } from 'react-redux'
import { GreenButton } from '../../../components/Buttons/GreenButton'
import { IData } from '../UserProfileTabs'
import { IntroducerApproveModal } from './IntroducerApproveModal'
import { IntroducerRejectModal } from './IntroducerRejectModal'
import { IntroducerAssignModal } from './IntroducerAssignModal'
import { IntroducerReassignModal } from './IntroducerReassignModal'
import { IntroducerUsersTable } from './IntroducerUsersTable'
import { formatIntroducerValue } from '../../../utils/formatCurrency'
import { IntroducerSuspendModal } from './IntroducerSuspendModal'
import { deleteIntroducerUserRequest } from '../../../store/actions'
import { AppStateContext } from '../../../components/Context'
import { routes } from '../../../constants/routes'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    marginTop: theme.spacing(4),
    padding: theme.spacing(4),
    '& .MuiGrid-item:not(:last-child)': {
      paddingBottom: theme.spacing(3),
    },
    '& .MuiTypography-body1': {
      color: theme.palette.primary.light,
      display: 'inline',
    },
    '& span': {
      fontWeight: 'bold',
    },
  },
}))

export const IntroducerPanel: FC<{
  data?: IData
  refreshProfile?: (value: boolean) => void
}> = (props) => {
  const {
    introducerCommission = 0,
    introducerBankFee = 0,
    introducerLiquidityFee = 0,
    introducerStatus,
    introducerApprovedBy,
  } = pick(props.data, [
    'introducerCommission',
    'introducerBankFee',
    'introducerLiquidityFee',
    'introducerStatus',
    'introducerApprovedBy',
  ])
  const isUserRoute = !!useRouteMatch(routes.USER_DETAILS_TABS)
  const introducerId = isUserRoute ? props.data?.id : props.data?.adminId
  const idSf = isUserRoute ? props.data?.idSf : props.data?.adminIdSf

  const { getUserId } = useContext(AppStateContext)
  const dispatch = useDispatch()
  const { refreshProfile } = props

  const classes = useStyles()
  const history = useHistory()
  const [introducerApproveModalOpen, setIntroducerApproveModalOpen] = useState(false)
  const [introducerRejectModalOpen, setIntroducerRejectModalOpen] = useState(false)
  const [introducerSuspendModalOpen, setIntroducerSuspendModalOpen] = useState(false)
  const [introducerAssignModalOpen, setIntroducerAssignModalOpen] = useState(false)
  const [introducerReassignModalOpen, setIntroducerReassignModalOpen] = useState(false)
  const [reassignId, setReassignId] = useState<string | null>(null)

  const handleIntroducerApproveModalOpen = useCallback(() => {
    setIntroducerApproveModalOpen(true)
  }, [])

  const handleIntroducerApproveModalClose = useCallback(() => {
    setIntroducerApproveModalOpen(false)
  }, [])

  const handleIntroducerRejectModalOpen = useCallback(() => {
    setIntroducerRejectModalOpen(true)
  }, [])

  const handleIntroducerRejectModalClose = useCallback(() => {
    setIntroducerRejectModalOpen(false)
  }, [])

  const handleIntroducerSuspendModalOpen = useCallback(() => {
    setIntroducerSuspendModalOpen(true)
  }, [])

  const handleIntroducerSuspendModalClose = useCallback(() => {
    setIntroducerSuspendModalOpen(false)
  }, [])

  const handleIntroducerReassignModalClose = useCallback(() => {
    setReassignId(null)
    setIntroducerReassignModalOpen(false)
  }, [])

  const handleIntroducerAssignModalOpen = useCallback(() => {
    setIntroducerAssignModalOpen(true)
  }, [])

  const handleIntroducerAssignModalClose = useCallback(() => {
    setReassignId(null)
    setIntroducerAssignModalOpen(false)
  }, [])

  const handleIntroducerReassign = useCallback((id: string) => {
    setReassignId(id)
    setIntroducerReassignModalOpen(true)
  }, [])

  const handleIntroducerRemove = useCallback(
    (id: number) => {
      dispatch(deleteIntroducerUserRequest({ id }))
    },
    [dispatch],
  )

  const handleRefreshProfile = useCallback(() => {
    if (refreshProfile) refreshProfile(true)
  }, [refreshProfile])

  useEffect(() => {
    history.push('?page=1')
  }, [history])

  return (
    <>
      <Grid container className={classes.container}>
        <Grid container item xs={12}>
          <Grid item xs={10}>
            <Grid item xs={12}>
              <Typography variant="h6">Settings</Typography>
            </Grid>
            {introducerStatus !== IntroducerStatusEnum.NOT_INITIAL && (
              <Grid item xs={6}>
                <Typography variant="body1">Status: </Typography>
                <Typography component="span">
                  {introducerStatus === IntroducerStatusEnum.REJECTED ? 'DECLINED' : introducerStatus}
                </Typography>
              </Grid>
            )}
            <Grid item xs={6}>
              <Typography variant="body1">Commission: </Typography>
              <Typography component="span">{formatIntroducerValue(introducerCommission)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body1">Bank fee: </Typography>
              <Typography component="span">{formatIntroducerValue(introducerBankFee)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body1">Liquidity fee: </Typography>
              <Typography component="span">{formatIntroducerValue(introducerLiquidityFee)}</Typography>
            </Grid>
          </Grid>
          <Grid item xs={2}>
            <Grid container display="flex" flexDirection="column" justifyContent="space-between">
              {introducerStatus === IntroducerStatusEnum.APPROVED && (
                <Grid item display="flex" justifyContent="end">
                  <GreenButton
                    backgroundColor="#ccc"
                    text="Edit"
                    width="300"
                    onClick={handleIntroducerApproveModalOpen}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid container justifyContent="end">
            {introducerStatus !== IntroducerStatusEnum.REJECTED && (
              <Grid item display="flex" flexDirection="row-reverse" gap={3} xs={2}>
                {introducerStatus === IntroducerStatusEnum.APPROVED ? (
                  <GreenButton
                    backgroundColor="#E08E2C"
                    width="100%"
                    text="Suspend"
                    onClick={handleIntroducerSuspendModalOpen}
                  />
                ) : (
                  <>
                    <GreenButton
                      disabled={
                        introducerStatus === IntroducerStatusEnum.PRE_APPROVED && introducerApprovedBy === getUserId?.()
                      }
                      width="100%"
                      text="Approve"
                      onClick={handleIntroducerApproveModalOpen}
                    />
                    <GreenButton
                      backgroundColor="#C92A2A"
                      width="100%"
                      text="Decline"
                      onClick={handleIntroducerRejectModalOpen}
                    />
                  </>
                )}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Grid container className={classes.container}>
        <Grid item xs={12}>
          <Grid item xs={12} display={'flex'} justifyContent={'space-between'}>
            <Typography variant="h6">Users</Typography>
            {introducerStatus === IntroducerStatusEnum.APPROVED && (
              <GreenButton width={100} text={'Assign User'} onClick={handleIntroducerAssignModalOpen} />
            )}
          </Grid>
          <Grid item xs={12}>
            <IntroducerUsersTable
              idSf={idSf}
              introducerId={introducerId}
              reassignHandler={handleIntroducerReassign}
              removeHandler={handleIntroducerRemove}
            />
          </Grid>
        </Grid>
      </Grid>
      <IntroducerApproveModal
        isOpen={introducerApproveModalOpen}
        idSf={idSf}
        introducerCommission={introducerCommission}
        introducerBankFee={introducerBankFee}
        introducerLiquidityFee={introducerLiquidityFee}
        modalClose={handleIntroducerApproveModalClose}
        refresh={handleRefreshProfile}
      />
      <IntroducerRejectModal
        isOpen={introducerRejectModalOpen}
        idSf={idSf}
        modalClose={handleIntroducerRejectModalClose}
        refresh={handleRefreshProfile}
      />
      <IntroducerSuspendModal
        isOpen={introducerSuspendModalOpen}
        idSf={idSf}
        modalClose={handleIntroducerSuspendModalClose}
        refresh={handleRefreshProfile}
      />
      <IntroducerAssignModal
        isOpen={introducerAssignModalOpen}
        introducerId={introducerId}
        idSf={idSf}
        modalClose={handleIntroducerAssignModalClose}
      />
      <IntroducerReassignModal
        isOpen={introducerReassignModalOpen}
        introducerId={introducerId}
        idSf={idSf}
        modalClose={handleIntroducerReassignModalClose}
        reassignId={reassignId}
      />
    </>
  )
}
